import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { PRODUCTS, isProductKey, type ProductKey } from "@/platform/products";
import { platform } from "@/platform/runtime";
import { AuthPasswordField, AuthShell, AuthSubmit } from "@/platform/auth/AuthShell";
import { sharedPageProduct } from "@/platform/auth/brand";

/**
 * Pagina condivisa da tutti i prodotti (/auth/reset-password): arrivo dal link di recupero
 * password. Il client Supabase legge il token dall'URL e apre una sessione temporanea;
 * qui l'utente sceglie la nuova password e poi accede al suo prodotto.
 */
export default function ResetPassword() {
  const [ready, setReady] = useState<"checking" | "ok" | "invalid">("checking");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [doneProduct, setDoneProduct] = useState<ProductKey | null>(null);

  useEffect(() => {
    let cancelled = false;
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!cancelled && session && (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN")) setReady("ok");
    });
    // Il token può essere già stato letto prima della sottoscrizione
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      if (data.session) setReady("ok");
      else setTimeout(() => !cancelled && setReady((r) => (r === "checking" ? "invalid" : r)), 4000);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("La password deve contenere almeno 8 caratteri");
      return;
    }
    if (password !== confirm) {
      toast.error("Le due password non coincidono");
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      // Prodotto principale del tenant dell'utente, per proporre l'accesso giusto
      let home: ProductKey = platform.product && platform.product !== "console" ? platform.product : "construction";
      const { data: userData } = await supabase.auth.getUser();
      if (userData.user) {
        const { data: tenantId } = await supabase.rpc("get_user_tenant", { _user_id: userData.user.id });
        if (tenantId) {
          const { data: homeProduct } = await (
            supabase.rpc as unknown as (
              fn: string,
              args: Record<string, unknown>,
            ) => Promise<{ data: unknown }>
          )("tenant_home_product", { _tenant_id: tenantId });
          if (isProductKey(homeProduct)) home = homeProduct;
        }
      }
      // La sessione di recupero serve solo a cambiare la password
      await supabase.auth.signOut({ scope: "local" });
      setDoneProduct(home);
      toast.success("Password aggiornata");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Impossibile aggiornare la password");
    } finally {
      setSaving(false);
    }
  };

  const loginUrl = doneProduct
    ? `${platform.originFor(doneProduct)}${PRODUCTS[doneProduct].loginPath}`
    : null;

  const brandProduct = sharedPageProduct(platform.product, null);

  return (
    <AuthShell
      product={brandProduct}
      title={doneProduct ? "Password aggiornata" : "Imposta nuova password"}
      subtitle={
        doneProduct
          ? "Ora puoi accedere con la nuova password."
          : ready === "invalid"
            ? "Il link non è valido o è scaduto."
            : "Scegli una password di almeno 8 caratteri."
      }
    >
      {doneProduct && loginUrl ? (
        <div className="flex flex-col items-center gap-5 py-2">
          <CheckCircle2 className="h-10 w-10 text-green-600" />
          <AuthSubmit type="button" product={brandProduct} onClick={() => window.location.assign(loginUrl)}>
            Accedi a {PRODUCTS[doneProduct].name}
          </AuthSubmit>
        </div>
      ) : ready === "checking" ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : ready === "invalid" ? (
        <p className="py-2 text-center text-sm text-slate-600">
          Torna alla pagina di accesso e usa «Password dimenticata?» per riceverne uno nuovo.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <AuthPasswordField
            id="new-password"
            label="Nuova password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={saving}
            product={brandProduct}
          />
          <AuthPasswordField
            id="confirm-password"
            label="Ripeti la password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            disabled={saving}
            product={brandProduct}
          />
          <AuthSubmit product={brandProduct} loading={saving} loadingText="Salvataggio…">
            Salva nuova password
          </AuthSubmit>
        </form>
      )}
    </AuthShell>
  );
}
